/**
 * Browser-only F0 (pitch) extraction for the ASR comparison page.
 *
 * Decodes the recorded Blob with the AudioContext, decimates to 16kHz
 * mono, runs YIN pitch detection at 16ms hops, then summarises the
 * F0 contour into:
 *   - pitch range (semitones, 5–95 percentile, octave-error robust)
 *   - pitch variance (std in semitones)
 *   - voiced ratio (% of frames with a clean F0 estimate)
 *   - 0–100 prosody score (linear blend of the three above)
 *
 * The score targets one specific gap: native English speakers vary
 * pitch ~8–12 semitones across a sentence; Asian learners often
 * deliver in 2–4 st bands ("monotone English"). It does NOT measure
 * stress placement, statement-vs-question intonation matching, or
 * phoneme-level prosody. Those need reference-aligned analysis.
 *
 * YIN reference: de Cheveigné & Kawahara, JASA 2002.
 *
 * Why client-side: the recorded audio Blob is already in browser
 * memory; sending it back for prosody would double the upload and
 * cost API minutes. YIN at 16kHz / 1024-sample frames runs in
 * ~100–300ms for a 5-second clip on modern laptops.
 */

const SAMPLE_RATE = 16000;
const FRAME_SIZE = 1024;        // 64ms — wide enough for F0 down to ~75Hz
const HOP_SIZE = 256;            // 16ms hop → ~62 frames/sec
const F0_MIN = 75;
const F0_MAX = 500;
// Original YIN paper recommends 0.10. Real-world browser recordings
// (WebM/Opus + auto-gain + noise suppression) need a more permissive
// threshold or YIN false-negatives many genuinely voiced frames —
// e.g. vowels in "I have a question about the assignment" came back
// as null at 0.15. 0.20 is the upper end of the literature range.
const YIN_THRESHOLD = 0.20;
const ENERGY_GATE = 0.01;

export interface ProsodyAnalysis {
  /** F0 in Hz per frame, null where unvoiced or silent. */
  f0Frames: Array<number | null>;
  /** Frame timing (sec) parallel to f0Frames. */
  frameTimes: number[];
  /** True at indices where the frame's RMS was below the energy
   *  gate (i.e., silence). Lets the visualiser distinguish
   *  silence-induced nulls from YIN-failed-to-lock nulls. */
  silent: boolean[];
  pitchRangeSt: number;
  pitchVarianceSt: number;
  voicedRatio: number;
  /** 0-100 prosody score. */
  score: number;
}

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

async function decodeAndResample(blob: Blob): Promise<Float32Array> {
  const arrayBuffer = await blob.arrayBuffer();
  const Ctor = window.AudioContext
    || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctor();
  let audioBuffer: AudioBuffer;
  try {
    audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  } finally {
    void ctx.close();
  }

  // Render through OfflineAudioContext to get *properly* anti-aliased
  // resampling at SAMPLE_RATE. The naive every-Nth-sample decimation
  // we used before folded 8–24kHz energy (fricatives, breath, room
  // noise) back into the speech band and tanked YIN's voicing rate
  // — many genuinely voiced frames came back as null because the
  // aliased noise destroyed the periodicity YIN looks for.
  // Asking for numberOfChannels=1 also handles stereo→mono downmix
  // automatically per the WebAudio spec.
  const Offline = window.OfflineAudioContext
    || (window as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  const offline = new Offline(
    1,
    Math.max(1, Math.ceil(audioBuffer.duration * SAMPLE_RATE)),
    SAMPLE_RATE,
  );
  const src = offline.createBufferSource();
  src.buffer = audioBuffer;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

/**
 * YIN pitch detector. Returns Hz, or null if the frame is unvoiced /
 * no minimum below the threshold was found within the speech band.
 */
function yinPitch(buffer: Float32Array, sampleRate: number): number | null {
  const halfSize = Math.floor(buffer.length / 2);
  const yin = new Float32Array(halfSize);

  for (let tau = 1; tau < halfSize; tau++) {
    let sum = 0;
    for (let i = 0; i < halfSize; i++) {
      const delta = buffer[i] - buffer[i + tau];
      sum += delta * delta;
    }
    yin[tau] = sum;
  }

  yin[0] = 1;
  let runningSum = 0;
  for (let tau = 1; tau < halfSize; tau++) {
    runningSum += yin[tau];
    yin[tau] *= tau / runningSum;
  }

  let tauEstimate = -1;
  for (let tau = 2; tau < halfSize; tau++) {
    if (yin[tau] < YIN_THRESHOLD) {
      // Descend to the local minimum so we lock onto the actual period.
      while (tau + 1 < halfSize && yin[tau + 1] < yin[tau]) tau++;
      tauEstimate = tau;
      break;
    }
  }
  if (tauEstimate === -1) return null;

  let betterTau: number = tauEstimate;
  if (tauEstimate > 0 && tauEstimate < halfSize - 1) {
    const s0 = yin[tauEstimate - 1];
    const s1 = yin[tauEstimate];
    const s2 = yin[tauEstimate + 1];
    const denom = 2 * (2 * s1 - s2 - s0);
    if (denom !== 0) betterTau = tauEstimate + (s2 - s0) / denom;
  }

  const freq = sampleRate / betterTau;
  if (freq < F0_MIN || freq > F0_MAX) return null;
  return freq;
}

function extractF0Contour(
  samples: Float32Array,
  sampleRate: number,
): {
  f0Frames: Array<number | null>;
  frameTimes: number[];
  /** True when the frame is at-or-below the energy gate (i.e. silent). */
  silent: boolean[];
} {
  const f0Frames: Array<number | null> = [];
  const frameTimes: number[] = [];
  const silent: boolean[] = [];
  const frame = new Float32Array(FRAME_SIZE);

  for (let start = 0; start + FRAME_SIZE < samples.length; start += HOP_SIZE) {
    let energy = 0;
    for (let i = 0; i < FRAME_SIZE; i++) {
      const s = samples[start + i];
      energy += s * s;
    }
    const rms = Math.sqrt(energy / FRAME_SIZE);
    frameTimes.push(start / sampleRate);
    if (rms < ENERGY_GATE) {
      f0Frames.push(null);
      silent.push(true);
      continue;
    }
    for (let i = 0; i < FRAME_SIZE; i++) frame[i] = samples[start + i];
    f0Frames.push(yinPitch(frame, sampleRate));
    silent.push(false);
  }
  return { f0Frames, frameTimes, silent };
}

function scoreContour(
  f0Frames: Array<number | null>,
  silent: boolean[],
): {
  pitchRangeSt: number;
  pitchVarianceSt: number;
  voicedRatio: number;
  score: number;
} {
  // Two-step gating to make voicedRatio actually mean something:
  //   1) Trim leading + trailing silence (don't penalise a learner
  //      who waited a beat before speaking).
  //   2) Within the speech region, use **non-silent** frames as the
  //      denominator — interior pauses (between words, stop closures)
  //      shouldn't count against the user. So voicedRatio is then
  //      "% of frames-where-the-user-was-actually-vocalising that YIN
  //      locked an F0 on" — i.e. voiced (vowels, /m/ /n/ /l/ /r/ /v/
  //      /z/) vs unvoiced (fricatives /s/ /f/ /h/, stop bursts).
  let firstNonSilent = 0;
  while (firstNonSilent < silent.length && silent[firstNonSilent]) firstNonSilent++;
  let lastNonSilent = silent.length - 1;
  while (lastNonSilent > firstNonSilent && silent[lastNonSilent]) lastNonSilent--;

  const speechFrames = f0Frames.slice(firstNonSilent, lastNonSilent + 1);
  const speechSilent = silent.slice(firstNonSilent, lastNonSilent + 1);
  const nonSilentInSpeech = speechSilent.filter(s => !s).length;

  const voiced = speechFrames.filter(
    (f): f is number => f !== null && f > F0_MIN && f < F0_MAX,
  );
  const voicedRatio = voiced.length / Math.max(nonSilentInSpeech, 1);

  if (voiced.length < 5) {
    return { pitchRangeSt: 0, pitchVarianceSt: 0, voicedRatio, score: 0 };
  }

  // Reference is arbitrary — semitone math only cares about ratios.
  const semitones = voiced.map(f => 12 * Math.log2(f / 100));

  // 5-95 percentile range so a single octave-error frame can't blow
  // the range up.
  const sorted = [...semitones].sort((a, b) => a - b);
  const p5 = sorted[Math.floor(sorted.length * 0.05)];
  const p95 = sorted[Math.floor(sorted.length * 0.95)];
  const pitchRangeSt = Math.max(0, p95 - p5);

  const mean = semitones.reduce((a, b) => a + b, 0) / semitones.length;
  const variance = semitones.reduce((s, x) => s + (x - mean) ** 2, 0) / semitones.length;
  const pitchVarianceSt = Math.sqrt(variance);

  // Calibration aimed at the "monotone vs dynamic" axis:
  //   range ≥ 8 st  → full marks (typical native speech)
  //   variance ≥ 2.5 st → full marks
  //   voiced ratio ≥ 0.65 → full marks. Now that the denominator is
  //   *non-silent frames* (not all frames in the speech region),
  //   natural English sits ~60-75% (vowels + voiced consonants
  //   /m n l r v z d g b w y/), unvoiced ~25-40% (fricatives
  //   /s f h/, stop bursts /p t k/). 65% is a fair native target.
  const rangeScore = clamp(pitchRangeSt / 8, 0, 1);
  const varianceScore = clamp(pitchVarianceSt / 2.5, 0, 1);
  const voicingScore = clamp(voicedRatio / 0.65, 0, 1);

  const score = Math.round(40 * rangeScore + 35 * varianceScore + 25 * voicingScore);

  return { pitchRangeSt, pitchVarianceSt, voicedRatio, score };
}

export async function analyzeProsody(blob: Blob): Promise<ProsodyAnalysis> {
  const samples = await decodeAndResample(blob);
  const { f0Frames, frameTimes, silent } = extractF0Contour(samples, SAMPLE_RATE);
  const summary = scoreContour(f0Frames, silent);
  return { f0Frames, frameTimes, silent, ...summary };
}
